import cn from "classnames";
import { memo, ReactNode, RefObject, useEffect, useRef, useState } from "react";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { UseMediaStreamResult } from "../../hooks/use-media-stream-mux";
import { useScreenCapture } from "../../hooks/use-screen-capture";
import { useWebcam } from "../../hooks/use-webcam";
import { AudioRecorder } from "../../lib/audio-recorder";
import AudioPulse from "../audio-pulse/AudioPulse";
import "./control-tray.scss";
export type ControlTrayProps = {
videoRef: RefObject<HTMLVideoElement>;
children?: ReactNode;
supportsVideo: boolean;
onVideoStreamChange?: (stream: MediaStream | null) => void;
};
type MediaStreamButtonProps = {
isStreaming: boolean;
onIcon: string;
offIcon: string;
start: () => Promise<any>;
stop: () => any;
};
const MediaStreamButton = memo(
({ isStreaming, onIcon, offIcon, start, stop }: MediaStreamButtonProps) =>
isStreaming ? (
<button className="action-button btn btn-secondary" onClick={stop} aria-pressed={isStreaming}>
<span className="material-symbols-outlined">{onIcon}</span>
</button>
) : (
<button className="action-button btn btn-secondary" onClick={start} aria-pressed={isStreaming}>
<span className="material-symbols-outlined">{offIcon}</span>
</button>
),
);
function ControlTray({
videoRef,
children,
onVideoStreamChange = () => {},
supportsVideo,
}: ControlTrayProps) {
const videoStreams = [useWebcam(), useScreenCapture()];
const [activeVideoStream, setActiveVideoStream] = useState<MediaStream | null>(null);
const [webcam, screenCapture] = videoStreams;	
const [facingMode, setFacingMode] = useState<string | null>(webcam.facingMode);
const [inVolume, setInVolume] = useState(0);
const [audioRecorder] = useState(() => new AudioRecorder());
const [muted, setMuted] = useState(false);
const renderCanvasRef = useRef<HTMLCanvasElement>(null);
const connectButtonRef = useRef<HTMLButtonElement>(null);
const [isScreenShareSupported, setIsScreenShareSupported] = useState(false);
const startAudio = new Audio('/voice_start.mp3');
const endAudio = new Audio('/voice_success.mp3');
const [toast, setToast] = useState<string>("");
const sendToast = (content: string, timer=3000) => {
setToast(content);
setTimeout(() => setToast(""), timer);
}
const { client, connected, connect, disconnect, volume } = useLiveAPIContext();
useEffect(() => {
if ('getDisplayMedia' in navigator.mediaDevices) {
setIsScreenShareSupported(true);
}
if (!connected && connectButtonRef.current) {

}
}, [connected, isScreenShareSupported]);
useEffect(() => {
document.documentElement.style.setProperty(
"--volume",
`${Math.max(5, Math.min(inVolume * 200, 8))}px`,
);
}, [inVolume]);
useEffect(() => {
const onData = (base64: string) => {
client.sendRealtimeInput([
{
mimeType: "audio/pcm;rate=16000",
data: base64,
},
]);
};
if (connected && !muted && audioRecorder) {
audioRecorder.on("data", onData).on("volume", setInVolume).start();
} else {
audioRecorder.stop();
}
return () => {
audioRecorder.off("data", onData).off("volume", setInVolume);
};
}, [connected, client, muted, audioRecorder]);
useEffect(() => {
if (videoRef.current) {
videoRef.current.srcObject = activeVideoStream;
}
let timeoutId = -1;
function sendVideoFrame() {
const video = videoRef.current;
const canvas = renderCanvasRef.current;
if (!video || !canvas) {
return;
}
const ctx = canvas.getContext("2d")!;
canvas.width = video.videoWidth * 0.25;
canvas.height = video.videoHeight * 0.25;
if (canvas.width + canvas.height > 0) {
ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
const base64 = canvas.toDataURL("image/jpeg", 1.0);
const data = base64.slice(base64.indexOf(",") + 1, Infinity);
client.sendRealtimeInput([{ mimeType: "image/jpeg", data }]);
}
if (connected) {
timeoutId = window.setTimeout(sendVideoFrame, 1000 / 0.5);
}
}
if (connected && activeVideoStream !== null) {
requestAnimationFrame(sendVideoFrame);
}
return () => {
clearTimeout(timeoutId);
};
}, [connected, activeVideoStream, client, videoRef]);
const changeStreams = (next?: UseMediaStreamResult) => async () => {
if (next) {
const mediaStream = await next.start();
setActiveVideoStream(mediaStream);
onVideoStreamChange(mediaStream);
} else {
setActiveVideoStream(null);
onVideoStreamChange(null);
}
videoStreams.filter((msr) => msr !== next).forEach((msr) => msr.stop());
};
const changeFacingMode = async(fcm: string | null) => {
setFacingMode(fcm);
webcam.setFacingMode(fcm);
const mediaStream = await webcam.start();
setActiveVideoStream(mediaStream);
onVideoStreamChange(mediaStream);
}
const handleConnectionStatus = async() => {
if (connected) {
endAudio.play();
await disconnect();
sendToast("Stopped");
} else {
sendToast("Loading...");
// loadingAudio.play();
await connect();
startAudio.play();
setTimeout(() => {
client.send([{text: "Hi, welcome"}]);
}, 700);
}
}
return (
<section className="control-tray" role="region" aria-label="Control tray">
<canvas style={{ display: "none" }} ref={renderCanvasRef} />
<nav
className={cn("actions-nav row", { disabled: !connected })}
role="group"
aria-label="Navigation Controls"
>
<button
className={cn("action-button mic-button col-4 btn btn-outline-primary")}
onClick={() => setMuted(!muted)}
aria-pressed={muted}
>
{!muted ? (
<span className="material-symbols-outlined filled">Mute</span>
) : (
<span className="material-symbols-outlined filled">unmute</span>
)}
</button>
<div className="action-button no-action outlined">
<AudioPulse volume={volume} active={connected} hover={false} />
</div>
{supportsVideo && (
<>
{isScreenShareSupported ? (
<MediaStreamButton
isStreaming={screenCapture.isStreaming}
start={changeStreams(screenCapture)}
stop={changeStreams()}
onIcon="Stop Screen Share"
offIcon="Share your screen"
/>
) : (
<div className="btn-group">
<button role="tab" className={cn("btn", {"btn-primary": facingMode==='environment', "btn-secondary": facingMode==='user'})} aria-selected={facingMode==='environment'} onClick={() => changeFacingMode('environment')}>Back Camera</button>
<button role="tab" className={cn("btn", {"btn-primary": facingMode==='user', "btn-secondary": facingMode==='environment'})} aria-selected={facingMode==='user'} onClick={() => changeFacingMode('user')}>Front Camera</button>
</div>
)}
<MediaStreamButton
isStreaming={webcam.isStreaming}
start={changeStreams(webcam)}
stop={changeStreams()}
onIcon="Stop Camera Sharing"
offIcon="Share your camera"
/>
</>
)}
{children}
</nav>
<div className={cn("connection-container", { connected })}>
<div className="connection-button-container row">
<button
ref={connectButtonRef}
className={cn("action-button connect-toggle col-12 btn btn-lg", { connected })}
onClick={handleConnectionStatus}
>
<span className="material-symbols-outlined filled">
{connected ? "End" : "Start"}
</span>
</button>
</div>
{toast.length > 0 && (
<span role="alert" className="text-indicator text-danger">
{toast}
</span>
)}
</div>
</section>
);
}
export default memo(ControlTray);